import React, { useEffect, useState } from "react";
import interiorLibraryAdminApi from "../services/interiorLibraryAdminApi";

const EMPTY_FORM = {
  category_key: "",
  label: "",
  scope: "template",
  parent_key: "",
  description: "",
  sort_order: 0,
  is_active: true,
};

export default function InteriorCategoryManagerPage() {
  const [items, setItems] = useState([]);
  const [scope, setScope] = useState("template");
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);

  async function load() {
    const res = await interiorLibraryAdminApi.listCategories(
      scope ? { scope } : {}
    );
    setItems(res.items || []);
  }

  useEffect(() => {
    load();
  }, [scope]);

  function edit(item) {
    setEditingId(item.id);
    setForm({
      category_key: item.category_key || "",
      label: item.label || "",
      scope: item.scope || "template",
      parent_key: item.parent_key || "",
      description: item.description || "",
      sort_order: item.sort_order || 0,
      is_active: !!item.is_active,
    });
  }

  async function save() {
    if (!form.category_key || !form.label || !form.scope) {
      alert("Category key, label and scope are required.");
      return;
    }

    if (editingId) {
      await interiorLibraryAdminApi.updateCategory(editingId, form);
    } else {
      await interiorLibraryAdminApi.createCategory(form);
    }

    setEditingId(null);
    setForm(EMPTY_FORM);
    await load();
  }

  async function remove(item) {
    const ok = window.confirm(`Delete category "${item.label}"?`);
    if (!ok) return;

    await interiorLibraryAdminApi.deleteCategory(item.id);
    await load();
  }

  return (
    <div className="p-4 space-y-4 bg-slate-50 min-h-screen">
      <div>
        <h1 className="text-2xl font-semibold">Interior Categories</h1>
        <div className="text-sm text-slate-500">
          Central registry for editor categories, templates, hardware and future logic.
        </div>
      </div>

      <div className="flex gap-2 items-center">
        <label className="text-sm font-medium">Scope</label>
        <select
          value={scope}
          onChange={(e) => setScope(e.target.value)}
          className="px-3 py-2 border rounded bg-white"
        >
          <option value="">All</option>
          <option value="definition">Definition</option>
          <option value="template">Template</option>
          <option value="material">Material</option>
          <option value="hardware">Hardware</option>
        </select>
      </div>

      <div className="grid grid-cols-[420px_1fr] gap-4">
        <div className="bg-white border rounded p-4 space-y-3">
          <h2 className="font-semibold">
            {editingId ? "Edit Category" : "New Category"}
          </h2>

          <input
            value={form.category_key}
            onChange={(e) =>
              setForm((p) => ({ ...p, category_key: e.target.value }))
            }
            placeholder="category_key e.g. base.cabinet"
            className="w-full px-3 py-2 border rounded"
          />

          <input
            value={form.label}
            onChange={(e) =>
              setForm((p) => ({ ...p, label: e.target.value }))
            }
            placeholder="Label"
            className="w-full px-3 py-2 border rounded"
          />

          <select
            value={form.scope}
            onChange={(e) =>
              setForm((p) => ({ ...p, scope: e.target.value }))
            }
            className="w-full px-3 py-2 border rounded bg-white"
          >
            <option value="definition">Definition</option>
            <option value="template">Template</option>
            <option value="material">Material</option>
            <option value="hardware">Hardware</option>
          </select>

          <input
            value={form.parent_key}
            onChange={(e) =>
              setForm((p) => ({ ...p, parent_key: e.target.value }))
            }
            placeholder="Parent key optional"
            className="w-full px-3 py-2 border rounded"
          />

          <input
            type="number"
            value={form.sort_order}
            onChange={(e) =>
              setForm((p) => ({ ...p, sort_order: Number(e.target.value) }))
            }
            placeholder="Sort order"
            className="w-full px-3 py-2 border rounded"
          />

          <textarea
            value={form.description}
            onChange={(e) =>
              setForm((p) => ({ ...p, description: e.target.value }))
            }
            placeholder="Description"
            className="w-full px-3 py-2 border rounded h-24"
          />

          <label className="flex gap-2 items-center text-sm">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) =>
                setForm((p) => ({ ...p, is_active: e.target.checked }))
              }
            />
            Active
          </label>

          <div className="flex gap-2 justify-end">
            <button
              onClick={() => {
                setEditingId(null);
                setForm(EMPTY_FORM);
              }}
              className="px-4 py-2 border rounded"
            >
              Clear
            </button>

            <button
              onClick={save}
              className="px-4 py-2 bg-blue-600 text-white rounded"
            >
              Save
            </button>
          </div>
        </div>

        <div className="bg-white border rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-100">
              <tr>
                <th className="text-left p-2">Key</th>
                <th className="text-left p-2">Label</th>
                <th className="text-left p-2">Scope</th>
                <th className="text-left p-2">Sort</th>
                <th className="text-left p-2">Active</th>
                <th className="text-left p-2">Actions</th>
              </tr>
            </thead>

            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-t">
                  <td className="p-2 font-mono text-xs">{item.category_key}</td>
                  <td className="p-2">{item.label}</td>
                  <td className="p-2">{item.scope}</td>
                  <td className="p-2">{item.sort_order}</td>
                  <td className="p-2">{item.is_active ? "Yes" : "No"}</td>
                  <td className="p-2">
                    <div className="flex gap-2">
                      <button onClick={() => edit(item)} className="px-2 py-1 border rounded">
                        Edit
                      </button>
                      <button onClick={() => remove(item)} className="px-2 py-1 border rounded text-red-600">
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {!items.length && (
                <tr>
                  <td colSpan="6" className="p-4 text-slate-400">
                    No categories found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}